import type { CloudLoggingQueryRequest, GuidedCloudLoggingQueryRequest } from './logging.types';
import { stringifyLosslessJson } from './lossless-json-codec';

function quoteCloudLoggingString(value: string): string {
    return stringifyLosslessJson(value);
}

function compileRepeatedEquality(field: string, values: readonly string[]): string | undefined {
    const normalizedValues: string[] = [...new Set(values)].sort();
    if (normalizedValues.length === 0) {
        return undefined;
    }

    const quotedValues: string[] = normalizedValues.map(quoteCloudLoggingString);
    if (quotedValues.length === 1) {
        return `${field} = ${quotedValues[0]}`;
    }

    return `${field} = (${quotedValues.join(' OR ')})`;
}

function compileRepeatedFunction(name: string, values: readonly string[]): string | undefined {
    const normalizedValues: string[] = [...new Set(values)].sort();
    if (normalizedValues.length === 0) {
        return undefined;
    }

    const calls: string[] = normalizedValues.map(
        (value: string): string => `${name}(${quoteCloudLoggingString(value)})`
    );
    return calls.length === 1 ? calls[0] : `(${calls.join(' OR ')})`;
}

function compileGuidedCloudLoggingFilter(request: GuidedCloudLoggingQueryRequest): string {
    const clauses: Array<string | undefined> = [
        `timestamp >= ${quoteCloudLoggingString(request.start_timestamp)}`,
        `timestamp <= ${quoteCloudLoggingString(request.end_timestamp)}`,
        request.text ? `SEARCH(${quoteCloudLoggingString(request.text)})` : undefined,
        compileRepeatedEquality('severity', request.severities ?? []),
        compileRepeatedEquality('resource.type', request.resource_types ?? []),
        compileRepeatedFunction('log_id', request.log_names ?? []),
        request.trace_id ? `trace = ${quoteCloudLoggingString(request.trace_id)}` : undefined,
    ];

    return clauses
        .filter((clause: string | undefined): clause is string => clause !== undefined)
        .join(' AND ');
}

export function compileCloudLoggingFilter(request: CloudLoggingQueryRequest): string {
    switch (request.mode) {
        case 'guided':
            return compileGuidedCloudLoggingFilter(request);
        case 'raw':
            return request.filter;
        default: {
            const exhaustiveRequest: never = request;
            return exhaustiveRequest;
        }
    }
}
